import type { ColumnDef } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@nesso/ui/primitives/tooltip';

import { getSessionStatusIcon, type SessionListItem } from '@/entities/session';
import { formatDurationLabel, formatTime } from '@/shared/lib/time-utils';
import { TruncatedTextWithTooltip } from '@/shared/ui/text/truncated-text-with-tooltip';

export const pastSessionsTableColumns: ColumnDef<SessionListItem>[] = [
    {
        accessorKey: 'clientName',
        header: 'Client',
        cell: ({ row }) => {
            const session = row.original;
            const clientName = session.clients[0]?.name ?? 'Unknown client';
            const statusInfo = getSessionStatusIcon(session);

            const date = new Date(session.date);
            const timeStr = formatTime(date);
            const durationStr = formatDurationLabel(session.duration);

            return (
                <div className="min-w-0 flex-1 w-full">
                    <TruncatedTextWithTooltip text={clientName} className={statusInfo?.className} />
                    <span className="block w-full text-muted-foreground text-sm truncate xs:hidden">
                        {timeStr} ⋅ {durationStr}
                    </span>
                </div>
            );
        },
        meta: {
            className: 'whitespace-normal pl-0 xs:pl-2',
        },
    },
    {
        accessorKey: 'time',
        header: 'Time',
        cell: ({ row }) => {
            const session = row.original;
            const date = new Date(session.date);
            const timeStr = formatTime(date);
            const durationStr = formatDurationLabel(session.duration);

            return (
                <TruncatedTextWithTooltip
                    text={`${timeStr} ⋅ ${durationStr}`}
                    className="text-muted-foreground"
                />
            );
        },
        meta: {
            className: 'hidden xs:flex',
        },
    },
    {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
            const statusIcon = getSessionStatusIcon(row.original);
            if (!statusIcon) return null;

            const Icon = statusIcon.Icon;

            return (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className="flex items-center gap-2">
                            <Icon className={statusIcon.iconClassName} />
                            <span>{statusIcon.label}</span>
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>{statusIcon.label}</TooltipContent>
                </Tooltip>
            );
        },
        meta: {
            className: 'min-w-22',
        },
    },
    {
        id: 'action',
        header: '',
        cell: () => (
            <div className="flex w-full h-full justify-center items-center">
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
        ),
        meta: {
            className: 'justify-center w-[3.25rem]',
        },
    },
];
